import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../utils/api';
import './Recruiter.css';

const PAGE_SIZE = 8;
const initialForm = {
  question_text: '',
  option_a: '',
  option_b: '',
  option_c: '',
  option_d: '',
  option_e: '',
  correct_answer: 'a',
  points: 10,
  photo: null
};

export default function QuestionManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [selectedExamId, setSelectedExamId] = useState(location.state?.examId || '');
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [page, setPage] = useState(1);
  const [excelFile, setExcelFile] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState(initialForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    const loadExams = async () => {
      setLoadingExams(true);
      try {
        const res = await API.get('/recruiter/exams');
        setExams(res.data || []);
        if (!selectedExamId && res.data?.length > 0) {
          setSelectedExamId(res.data[0].id);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingExams(false);
      }
    };
    loadExams();
  }, [selectedExamId]);

  useEffect(() => {
    if (!selectedExamId) return;
    const loadQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const res = await API.get(`/recruiter/exam/${selectedExamId}/questions`);
        setQuestions(res.data || []);
        setPage(1);
        setSelectedQuestionIds([]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [selectedExamId]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(photoFile);
  }, [photoFile]);

  const pagedQuestions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return questions.slice(start, start + PAGE_SIZE);
  }, [questions, page]);

  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));

  const downloadTemplate = () => {
    const headers = [
      'question_text',
      'option_a',
      'option_b',
      'option_c',
      'option_d',
      'option_e',
      'correct_answer',
      'points',
      'photo'
    ];
    const sample = [
      '"Contoh pertanyaan?"',
      '"Pilihan A"',
      '"Pilihan B"',
      '"Pilihan C"',
      '"Pilihan D"',
      '"Pilihan E"',
      'a',
      '10',
      '"")'
    ];
    const csv = `${headers.join(',')}\n${sample.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_soal.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const verifyQuestion = () => {
    if (!questionForm.question_text.trim()) {
      alert('Pertanyaan harus diisi');
      return false;
    }
    const options = ['option_a', 'option_b', 'option_c', 'option_d', 'option_e'];
    const filled = options.filter(opt => questionForm[opt]?.trim());
    if (filled.length < 2) {
      alert('Isi minimal 2 opsi jawaban');
      return false;
    }
    const correctKey = `option_${questionForm.correct_answer}`;
    if (!questionForm[correctKey]?.trim()) {
      alert('Jawaban benar harus dipilih dari opsi yang tersedia');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setQuestionForm(initialForm);
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowForm(false);
  };

  const saveQuestion = async () => {
    if (!verifyQuestion()) return;
    if (!selectedExamId) {
      alert('Pilih ujian terlebih dahulu');
      return;
    }
    try {
      if (editingQuestion) {
        await API.put(`/recruiter/question/${editingQuestion.id}`, {
          ...questionForm,
        });
        if (photoFile) {
          const formData = new FormData();
          formData.append('question_id', editingQuestion.id);
          formData.append('photo', photoFile);
          await API.post('/recruiter/question/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        alert('Soal berhasil diperbarui');
      } else {
        const res = await API.post('/recruiter/question', {
          exam_id: selectedExamId,
          ...questionForm
        });
        if (res.data.question_id && photoFile) {
          const formData = new FormData();
          formData.append('question_id', res.data.question_id);
          formData.append('photo', photoFile);
          await API.post('/recruiter/question/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        alert('Soal berhasil dibuat');
      }
      resetForm();
      const res = await API.get(`/recruiter/exam/${selectedExamId}/questions`);
      setQuestions(res.data);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Hapus soal ini?')) return;
    try {
      await API.delete(`/recruiter/question/${questionId}`);
      const res = await API.get(`/recruiter/exam/${selectedExamId}/questions`);
      setQuestions(res.data);
      setSelectedQuestionIds(prev => prev.filter(id => id !== questionId));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedQuestionIds(questions.map(q => q.id));
    } else {
      setSelectedQuestionIds([]);
    }
  };

  const toggleSelectQuestion = (questionId, checked) => {
    setSelectedQuestionIds(prev => checked ? [...new Set([...prev, questionId])] : prev.filter(id => id !== questionId));
  };

  const bulkDelete = async () => {
    if (selectedQuestionIds.length === 0) {
      alert('Pilih minimal satu soal');
      return;
    }
    if (!window.confirm('Hapus semua soal terpilih?')) return;
    try {
      await API.post('/recruiter/questions/bulk-delete', { question_ids: selectedQuestionIds });
      const res = await API.get(`/recruiter/exam/${selectedExamId}/questions`);
      setQuestions(res.data);
      setSelectedQuestionIds([]);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const uploadExcel = async () => {
    if (!excelFile) {
      alert('Pilih file Excel terlebih dahulu');
      return;
    }
    if (!selectedExamId) {
      alert('Pilih ujian terlebih dahulu');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('exam_id', selectedExamId);
      const res = await API.post('/recruiter/questions/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(res.data.message);
      setExcelFile(null);
      const qres = await API.get(`/recruiter/exam/${selectedExamId}/questions`);
      setQuestions(qres.data);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const beginEdit = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      question_text: question.question_text,
      option_a: question.option_a || '',
      option_b: question.option_b || '',
      option_c: question.option_c || '',
      option_d: question.option_d || '',
      option_e: question.option_e || '',
      correct_answer: question.correct_answer,
      points: question.points || 10,
      photo: question.photo || null
    });
    setPhotoFile(null);
    setPhotoPreview(question.photo ? `/uploads/questions/${question.photo}` : null);
    setShowForm(true);
  };

  return (
    <div className="rec-page">
      <header className="rec-header">
        <div className="rec-logo">📝 Manajemen Soal</div>
        <div className="rec-user">
          <button className="btn-secondary" onClick={() => navigate('/recruiter')}>← Kembali</button>
        </div>
      </header>

      <div className="rec-body">
        <div className="question-manager-panel">
          <div className="panel-header">
            <div>
              <h2>Soal Ujian</h2>
              <p className="panel-subtitle">Tata ulang soal dalam tabel dengan pagination dan panduan upload.</p>
            </div>
            <div className="panel-actions">
              <button className="btn-primary" onClick={() => setShowForm(true)}>➕ Tambah Soal</button>
              <button className="btn-secondary" onClick={downloadTemplate}>Download Template</button>
            </div>
          </div>

          <div className="question-manager-controls">
            <label className="select-exam-label">
              Pilih Ujian:
              <select value={selectedExamId} onChange={(e) => setSelectedExamId(e.target.value)}>
                <option value="">-- Pilih Ujian --</option>
                {exams.map(exam => (
                  <option key={exam.id} value={exam.id}>{exam.title}</option>
                ))}
              </select>
            </label>
            <div className="upload-row">
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setExcelFile(e.target.files?.[0] || null)} />
              <button className="btn-primary" onClick={uploadExcel}>Upload Excel</button>
            </div>
          </div>

          <div className="upload-help">
            <strong>Contoh format upload Excel:</strong>
            <p>Gunakan header kolom berikut pada baris pertama:</p>
            <code>question_text, option_a, option_b, option_c, option_d, option_e, correct_answer, points, photo</code>
            <p>Contoh baris:</p>
            <code>"Apa ibu kota Jawa Barat?","Bekasi","Bandung","Bogor","Depok","",b,10,""</code>
            <p><em>Field <strong>option_e</strong> dan <strong>photo</strong> bersifat opsional.</em></p>
          </div>

          {showForm && (
            <div className="form-modal">
              <div className="form-box large">
                <div className="form-modal-header">
                  <h3>{editingQuestion ? 'Edit Soal' : 'Soal Baru'}</h3>
                  <button className="close-btn" onClick={resetForm}>✕</button>
                </div>
                <div className="form-group">
                  <label>Pertanyaan</label>
                  <textarea
                    rows="3"
                    value={questionForm.question_text}
                    onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                    placeholder="Tulis pertanyaan..."
                  />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Opsi A</label>
                    <input type="text" value={questionForm.option_a} onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})} placeholder="Opsi A" />
                  </div>
                  <div className="form-group">
                    <label>Opsi B</label>
                    <input type="text" value={questionForm.option_b} onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})} placeholder="Opsi B" />
                  </div>
                  <div className="form-group">
                    <label>Opsi C</label>
                    <input type="text" value={questionForm.option_c} onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})} placeholder="Opsi C" />
                  </div>
                  <div className="form-group">
                    <label>Opsi D</label>
                    <input type="text" value={questionForm.option_d} onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})} placeholder="Opsi D" />
                  </div>
                  <div className="form-group">
                    <label>Opsi E</label>
                    <input type="text" value={questionForm.option_e} onChange={(e) => setQuestionForm({...questionForm, option_e: e.target.value})} placeholder="Opsi E (opsional)" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Jawaban Benar</label>
                    <select value={questionForm.correct_answer} onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}>
                      <option value="a">A</option>
                      <option value="b">B</option>
                      <option value="c">C</option>
                      <option value="d">D</option>
                      <option value="e">E</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Poin</label>
                    <input type="number" min="1" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Foto Soal (opsional)</label>
                  <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
                  {photoPreview && (
                    <div className="preview-box">
                      <img src={photoPreview} alt="Preview soal" />
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <button className="btn-save" onClick={saveQuestion}>Simpan</button>
                  <button className="btn-cancel" onClick={resetForm}>Batal</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-panel">
            <div className="table-actions">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.length === questions.length && questions.length > 0}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
                Pilih semua
              </label>
              <button className="btn-danger" onClick={bulkDelete}>Hapus Terpilih</button>
              <div className="table-info">Menampilkan {questions.length} soal. Halaman {page} dari {totalPages}.</div>
            </div>

            <div className="question-table-wrapper">
              <table className="question-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>No</th>
                    <th>Pertanyaan</th>
                    <th>Opsi</th>
                    <th>Benar</th>
                    <th>Poin</th>
                    <th>Gbr</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingQuestions ? (
                    <tr><td colSpan="8" className="empty-row">Memuat soal...</td></tr>
                  ) : pagedQuestions.length === 0 ? (
                    <tr><td colSpan="8" className="empty-row">Tidak ada soal pada ujian ini.</td></tr>
                  ) : pagedQuestions.map((q, idx) => (
                    <tr key={q.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedQuestionIds.includes(q.id)}
                          onChange={(e) => toggleSelectQuestion(q.id, e.target.checked)}
                        />
                      </td>
                      <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="q-summary">{q.question_text}</td>
                      <td>{['a','b','c','d','e'].filter(opt => q[`option_${opt}`]) .map(opt => opt.toUpperCase()).join(', ')}</td>
                      <td>{q.correct_answer?.toUpperCase()}</td>
                      <td>{q.points}</td>
                      <td>{q.photo ? 'Ya' : 'Tidak'}</td>
                      <td className="action-cell">
                        <button className="btn-secondary small" onClick={() => beginEdit(q)}>Edit</button>
                        <button className="btn-danger small" onClick={() => deleteQuestion(q.id)}>Hapus</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button className="page-btn" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>← Sebelumnya</button>
              <span>{page} / {totalPages}</span>
              <button className="page-btn" onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>Berikutnya →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
